import { CurrentVip } from '@/store/User';

import { Commodity, UserIdentity } from '../const';
import { LimitConfig } from '../type';

export interface UseValidateReturn {
  /**
   * @description 获取当前用户身份
   */
  getIdentity: () => UserIdentity;
  /**
   * @description 获取当前用户权益配置
   */
  getRightConfig: () => LimitConfig[UserIdentity];
  /**
   * @param {string} entry 可选值：
   * - subtitleTranslate 视频字幕翻译 - boolean
   * - audioTranslate 音频翻译 - boolean
   * - digitalHumanVideo 数字人视频 - boolean
   * - videoFaceMerge 视频换脸 - boolean
   * - subtitleRemove 字幕擦除 - boolean
   * - textEdit 是否允许字体编辑 - boolean
   * - uploadResolution 导入分辨率限制 - number
   * - exportResolution 导出帧率限制 - number
   * - duration 单文件最大时长 - number
   * - batchNum 批量数量限制 - number
   */
  getEntryValue: <T extends keyof LimitConfig[UserIdentity]>(
    entry: T,
  ) => LimitConfig[UserIdentity][T];

  /**
   * @description 获取用户单次购买商品配置
   */
  getSinglePurchaseCommodity: () => Commodity;

  /**
   * @description 获取下一等级的配置
   */
  getNextLevel: () => {
    key: UserIdentity;
    config: LimitConfig[UserIdentity];
  };
  /**
   * @description 获取上一等级的配置
   */
  getPreviousLevel: () => {
    key: UserIdentity;
    config: LimitConfig[UserIdentity];
  };
}
interface UseValidate {
  (
    limitConfig: LimitConfig,
    singlePurchaseConfig: Commodity,
    currentVip: CurrentVip,
  ): UseValidateReturn;
}
export const getValidateUtils: UseValidate = (
  limitConfig: LimitConfig,
  singlePurchaseConfig: Commodity,
  currentVip: CurrentVip,
) => {
  /**
   * 获取用户身份
   */
  const getIdentity = () => {
    return currentVip.key;
  };
  /**
   * @param {string} entry 可选值：
   * - subtitleTranslate 视频字幕翻译 - boolean
   * - audioTranslate 音频翻译 - boolean
   * - digitalHumanVideo 数字人视频 - boolean
   * - videoFaceMerge 视频换脸 - boolean
   * - subtitleRemove 字幕擦除 - boolean
   * - textEdit 是否允许字体编辑 - boolean
   * - uploadResolution 导入分辨率限制 - number
   * - exportResolution 导出帧率限制 - number
   * - duration 单文件最大时长 - number
   * - batchNum 批量数量限制 - number
   */
  const getEntryValue = <T extends keyof LimitConfig[UserIdentity]>(
    entry: T,
  ): LimitConfig[UserIdentity][T] => {
    return getRightConfig()[entry] ?? false;
  };

  /**
   * 获取用户身份配置
   */
  const getRightConfig = () => {
    return limitConfig[getIdentity()] || {};
  };

  /**
   * 获取用户单次购买商品配置
   */
  const getSinglePurchaseCommodity = () => {
    return singlePurchaseConfig;
  };

  /**
   *  获取下一等级的配置
   */
  const getNextLevel = () => {
    const currentConfig = getRightConfig();

    const result = Object.entries(limitConfig)
      .sort(([, a], [, b]) => a.weight - b.weight)
      .find(([key, config]) => {
        const diff = config.weight - currentConfig.weight;
        return diff > 0;
      });

    return result
      ? {
          key: result[0] as UserIdentity,
          config: result[1] as LimitConfig[UserIdentity],
        }
      : {
          key: '' as UserIdentity,
          config: {} as LimitConfig[UserIdentity],
        };
  };

  /**
   *  获取上一等级的配置
   */
  const getPreviousLevel = () => {
    const currentConfig = getRightConfig();

    const result = Object.entries(limitConfig)
      .sort(([, a], [, b]) => b.weight - a.weight)
      .find(([key, config]) => {
        const diff = currentConfig.weight - config.weight;
        return diff > 0;
      });

    return result
      ? {
          key: result[0] as UserIdentity,
          config: result[1] as LimitConfig[UserIdentity],
        }
      : {
          key: '' as UserIdentity,
          config: {} as LimitConfig[UserIdentity],
        };
  };

  return {
    getIdentity,
    getRightConfig,
    getSinglePurchaseCommodity,
    getEntryValue,
    getNextLevel,
    getPreviousLevel,
  };
};
